<template lang="html">
  <v-card v-bind="$attrs" v-on="$listeners" style="overflow-y: hidden;" flat>
    <template v-if="showToolbar">
      <v-toolbar flat height="auto">
        <div v-if="!enumeratedEditable">
          <h3
            v-if="title && title.length > 0"
            :class="{ 'text-capitalize': !disableCaps }"
            class="text-h6"
          >
            {{ capitalize(title) }}
          </h3>
          <h4
            v-if="subtitle && subtitle.length > 0"
            :class="{ 'text-capitalize': !disableCaps }"
            class="text-body-2 text--secondary custom-text-truncate-2"
          >
            {{ subtitle }}
          </h4>
        </div>
        <div v-else>
          <div class="d-flex inline-flex align-center">
            <div class="mr-2">
              <h3 
                :class="{ 'text-capitalize title-for-base-section': !disableCaps }" 
                class="text-h6 clickable"
                @click="$emit('click:edit'); if (!$listeners['click:edit']) goToFormStep();"
              >
                {{ capitalize(title) }}
              </h3>
            </div>
            <div>
              <v-btn 
                class="secondary"
                x-small
                @click="$emit('click:edit'); if (!$listeners['click:edit']) goToFormStep();"
                :ripple="false"
              >
                {{$t('buttons.edit')}}
              </v-btn>
            </div>
          </div>
          <h4
            v-if="subtitle && subtitle.length > 0"
            :class="{ 'text-capitalize': !disableCaps }"
            class="text-body-2 text--secondary custom-text-truncate-2"
          >
            {{ subtitle }}
          </h4>
        </div>
        <v-spacer />
        <v-btn
          v-if="dismissable"
          icon
          @click="$emit('click:close')"
        >
          <v-icon dark>mdi-close</v-icon>
        </v-btn>
        <v-menu v-if="hasInfoSlot">
          <template #activator="{ on }">
            <v-btn color="primary" icon v-on="on" @click="$emit('click:info')">
              <v-icon>mdi-information-outline</v-icon>
            </v-btn>
          </template>
          <slot name="infomenu" />
        </v-menu>
        <slot name="close" />
      </v-toolbar>
    </template>

    <div :class="{'base-section-text-container': !containContent}">
      <v-progress-circular
        v-if="loading"
        class="loading-icon"
        indeterminate
        color="primary"
      />
      <div :class="{'loading':loading}">
        <v-card-text :class="disablePadding ? 'pa-0' : reducePadding ? 'pa-2' : 'pa-4'">
          <slot />
        </v-card-text>

        <template v-if="hasActionSlot">
          <v-divider />
          <v-card-actions class="justify-end ">
            <slot name="actions" />
          </v-card-actions>
        </template>
      </div>
    </div>
  </v-card>
</template>

<script>
import { capitalize } from 'lodash';
export default {
  name: 'BaseSection',
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      // title prop MUST be used too
      type: String,
      default: undefined,
    },
    editable: {
      // Deprecated
      type: Boolean,
      default: false,
    },
    editRouteName: {
      type: String,
      default: undefined,
    },
    editId: {
      type: [String, Number],
      default: undefined,
    },
    editStep: {
      type: [String, Number],
      default: undefined,
    },
    disablePadding: {
      type: Boolean,
      default: false,
    },
    reducePadding: {
      type: Boolean,
      default: false,
    },
    disableCaps: {
      type: Boolean,
      default: false,
    },
    loadingScope: {
      type: String,
      default: undefined,
    },
    dismissable: {
      type: Boolean,
      default: false,
    },
    containContent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    loading() {
      if (!this.loadingScope) {
        return false;
      } else {
        
        return this.$store.getters?.isLoading({ scope: this.loadingScope });
      }
    },
    showToolbar() {
      return !!(
        (this.title && this.title.length > 0) ||
        this.hasInfoSlot ||
        this.enumeratedEditable
      );
    },
    hasInfoSlot() {
      return !!this.$slots['infomenu'] && !!this.$slots['infomenu'][0];
    },
    hasActionSlot() {
      return !!this.$slots['actions'] && !!this.$slots['actions'][0];
    },
    enumeratedEditable() {
      return this.$attrs['editable'] || this.editable;
    },
  },
  methods: {
    capitalize,
    goToFormStep() {
      // Check if we're in a nested route structure (like price_lists/:id/price_list_entries/:entryId)
      const route = this.$route;
      const params = { ...route.params };
      
      // Use provided editId or routeId from attrs, or fallback to the most specific ID in the route
      const editId = this.editId || this.$attrs['routeId'] || params[Object.keys(params).pop()];
      
      // Determine the correct route name
      const routeName = this.editRouteName || this.$attrs['editRoute'];
      
      // Ensure operating company parameters are preserved
      const operatingCompanyUrl = params.operatingCompanyUrl;
      const dashboardPageUrl = params.dashboardPageUrl;
      
      if (routeName) {
        this.$router.push({
          name: routeName,
          params: { 
            ...params, 
            id: params.id, 
            [Object.keys(params).pop()]: editId,
            operatingCompanyUrl,
            dashboardPageUrl
          },
          query: { step: this.editStep || 0, isFromShow: true, },
        });
      } else {
        // Fallback to the old behavior if no specific route name is provided
        console.warn('No editRouteName provided to BaseSection, using fallback routing logic');
        const currentRouteName = route.name;
        // Try to derive the edit route name from the current route
        const derivedEditRoute = currentRouteName ? currentRouteName.replace('Details', 'Edit').replace('Show', 'Edit') : null;
        
        this.$router.push({
          name: derivedEditRoute || (currentRouteName + 'Edit'),
          params: { 
            ...params,
            operatingCompanyUrl,
            dashboardPageUrl
          },
          query: { step: this.editStep || 0 },
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.v-toolbar::v-deep .v-toolbar__content {
  padding-left: 0px;
}
.clickable {
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.title-for-base-section {
  color: var(--v-titleForBaseSection-base) !important;
}

.custom-btn {
  &::before {
    opacity: 0 !important;
  }

  &:hover {
    text-decoration: underline;
  }

  // Targeting v-btn__content
  ::v-deep .v-btn__content {
    align-items: baseline;
  }
}
</style>
<style lang="css" scoped>
.loading {
  opacity: 0.0;
  pointer-events: none !important;
  cursor: pointer !important;
}
.loading-background-color {
  background-color: gray;
}
.base-section-text-container {
  display: grid;
}
.loading-icon {
  position: absolute !important;
  opacity: 1.0;
  z-index: 5;
  align-self: center;
  justify-self: center;
}
</style>
